import { useState } from "react";
import {
  Variation,
  VariationSelect,
} from "../features/orders/types/tesVariationTypes";

const useTesVariation = (variation: Variation) => {
  const getDisabledElements = (variation: Variation): Map<string, Set<string>> => {
    // Handle disabled and enabled elements
    const disabledElements = new Map<string, Set<string>>();

    // Check if element should be disabled.
    // This is done by checking if variationDefault has a value for the current element and
    // if if value is in list of disables of current element's options
    variation.elements.forEach((element) => {
      const matchingDefault = variation.variationDefaults.find(
        (variationDefault) => variationDefault[0] === element.id,
      );
      element.options.forEach((option) => {
        if (matchingDefault && matchingDefault[1] === option.id) {
          disabledElements.set(element.id, new Set(option.disables))
        }
      });
    });

    // Set disabled elements
    return disabledElements;
  };

  const [disabledElements, setDisabledElements] = useState<Map<string, Set<string>>>(
    getDisabledElements(variation),
  );

  const initializeDefaults = (variation: Variation): Map<string, string> => {
    const { elements, variationDefaults } = variation;

    const map = new Map();

    // TODO: FIX THIS BETTER
    if (variationDefaults.length === 0) {
      elements.forEach((element) => {
        map.set(element.id, "");
      });
      return map;
    } else {
      elements.forEach((element) => {
        const matchingDefault = variationDefaults.find((variationDefault) => {
          return (
            variationDefault[0] === element.id &&
            // Check if the variationDefault[1] is one of the options for the current element
            element.options?.some(
              (option) =>
                option.id === variationDefault[1] || variationDefault[1] === "",
            )
          );
        });
        map.set(element.id, matchingDefault ? matchingDefault[1] : "");
      });
    }

    return map;
  };

  const [variationSelectionMap, setVariationSelectionMap] = useState(
    initializeDefaults(variation),
  );

  const changeVariation = (
    elementId: string,
    value: string,
  ): Map<string, string> => {
    const element = variation.elements.find(
      (element) => element.id === elementId,
    );
    if (!element) throw new Error("element not found");

    let currentMap = variationSelectionMap;

    switch (element.typeName) {
      case "select": {
        const select = element as VariationSelect;

        currentMap = handleSelectChange(select, value, currentMap);
        break;
      }
      default: {
        console.error("unknown variation elementType");
        break;
      }
    }
    return currentMap;
  };

  const handleVariationChange = (elementId: string, value: string) => {
    const currentMap: Map<string, string> = changeVariation(elementId, value);

    setVariationSelectionMap(() => new Map(currentMap));
  };

  const handleSelectChange = (
    select: VariationSelect,
    optionId: string,
    variationMap: Map<string, string>,
  ): Map<string, string> => {
    let currentMap = variationMap;
    const option = select.options.find((option) => option.id === optionId);

    if (!option) {
      throw new Error("option not found");
    }

    currentMap.set(select.id, optionId);

    // disables for current select
    const myDisabledElements = new Set<string>();

    // Handle disables
    option.disables.forEach((disable) => {
      currentMap.set(disable, "");
      disabledElements.delete(disable);
      myDisabledElements.add(disable);
    });

    // NOTE: option.enables are deprecated

    disabledElements.set(select.id, myDisabledElements);
    setDisabledElements(disabledElements);
    // Return updated map
    return currentMap;
  };

  const handleDefaults = (variation: Variation) => {
    const map = initializeDefaults(variation);
    setVariationSelectionMap(() => new Map(map));
    return map;
  };

  const isValid = (): boolean => {
    let inValidElement = false;

    const disabledElementValues = new Set(Array.from(disabledElements.values()).flatMap(set => Array.from(set)))
    variationSelectionMap.forEach((value, key) => {
      const enabledElementHasNoValue =
        value === "" && !disabledElementValues.has(key);
      const disabledElementHasValue = value !== "" && disabledElementValues.has(key);
      if (enabledElementHasNoValue || disabledElementHasValue) {
        inValidElement = true;
      }
    });
    return !inValidElement;
  };

  return {
    variationSelectionMap,
    handleVariationChange,
    disabledElements,
    isValid,
    handleDefaults,
  };
};

export default useTesVariation;
